/**
 * Claim Action Modal Component
 * For approving, rejecting, verifying, or marking claims as paid
 */
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, ExternalLink } from 'lucide-react';
import { claimsAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ClaimActionModal = ({ 
  isOpen, 
  onClose, 
  claim, 
  action,
  onSuccess 
}) => {
  const [actionData, setActionData] = useState({
    rejection_reason: '',
    payment_reference: '',
    remarks: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setActionData({
      rejection_reason: '',
      payment_reference: '',
      remarks: ''
    });
  };

  const handleAction = async () => {
    if (!claim || !action) return;
    
    if (action === 'reject' && !actionData.rejection_reason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    if (action === 'pay' && !actionData.payment_reference.trim()) {
      toast.error('Please provide payment reference');
      return;
    }
    
    try {
      setSubmitting(true);
      await claimsAPI.processAction(claim.id, action, actionData);
      toast.success(`Claim ${action === 'approve' ? 'approved' : action === 'verify' ? 'verified' : action === 'reject' ? 'rejected' : 'marked as paid'}`);
      resetForm();
      onClose();
      onSuccess();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Failed to ${action} claim`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getActionTitle = () => {
    switch(action) {
      case 'approve': return 'Approve Claim';
      case 'reject': return 'Reject Claim';
      case 'verify': return 'Verify Claim';
      case 'pay': return 'Mark as Paid';
      default: return 'Process Claim';
    }
  };

  const getActionButtonText = () => {
    switch(action) {
      case 'approve': return 'Approve';
      case 'reject': return 'Reject';
      case 'verify': return 'Verify';
      case 'pay': return 'Mark Paid';
      default: return 'Submit';
    }
  };

  const getButtonVariant = () => {
    if (action === 'reject') return 'destructive';
    return 'default';
  };

  if (!claim) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{getActionTitle()}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="font-medium">{claim.title}</p>
            <p className="text-sm text-slate-500">{claim.category}</p>
            <p className="text-lg font-bold mt-2">{formatCurrency(claim.amount)}</p>
            <p className="text-xs text-slate-400">
              Submitted by {claim.employee_name || 'Unknown'} on {formatDate(claim.submitted_at || claim.created_at)}
            </p>
          </div>
          
          {/* Show attachment if exists */}
          {(claim.attachment_url || claim.receipt_url) && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-700 flex-1">
                {claim.attachment_name || 'Receipt attached'}
              </span>
              <a 
                href={claim.attachment_url || `${BACKEND_URL}/api/files/claim-receipt/${claim.receipt_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
          
          {action === 'reject' && (
            <div>
              <Label>Reason for Rejection *</Label>
              <Textarea
                value={actionData.rejection_reason}
                onChange={(e) => setActionData({...actionData, rejection_reason: e.target.value})}
                placeholder="Please provide a reason..."
                rows={3}
                data-testid="rejection-reason-input"
              />
            </div>
          )}
          
          {action === 'pay' && (
            <div>
              <Label>Payment Reference *</Label>
              <Input
                value={actionData.payment_reference}
                onChange={(e) => setActionData({...actionData, payment_reference: e.target.value})}
                placeholder="Transaction ID, UTR number, etc."
                data-testid="payment-reference-input"
              />
            </div>
          )}
          
          <div>
            <Label>Remarks (Optional)</Label>
            <Textarea
              value={actionData.remarks}
              onChange={(e) => setActionData({...actionData, remarks: e.target.value})}
              placeholder="Any additional notes..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            variant={getButtonVariant()}
            onClick={handleAction}
            disabled={submitting}
            data-testid={`${action}-claim-submit`}
          >
            {submitting ? 'Processing...' : getActionButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimActionModal;
